'use client';

import React, { memo, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { AlertCircle } from 'lucide-react';
import { PulsingDots } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { formatEstimateMoney } from '@/shared/lib/utils';
import type { EstimateStatus } from '../api/estimate-service';

interface EstimateDocumentViewProps {
  materialsTotal: number;
  laborTotal: number;
  grandTotal: number;
  /** Display currency (ISO 4217) — amounts arrive display-converted. */
  currency: string | null | undefined;
  /** Concept's project sequence number — rendered as small "Design #N". */
  conceptNumber?: number | null;
  createdAt?: string | null;
  status: EstimateStatus;
  className?: string;
}

/**
 * Estimate "document" cover — the estimate sibling of `AlbumCoverView`. A 4:3
 * white paper sheet drawn as a stylized table: localized "Cost estimate"
 * header, decorative line-item stripes, real materials/labor subtotals, a
 * grand total, and a small "Design #N • date" footer. No photo — the card IS
 * the visual. Sizes use container-query units (`cqi`) so the same proportions
 * hold at any card size (parity with Apple/Android document cards).
 */
export const EstimateDocumentView = memo(function EstimateDocumentView({
  materialsTotal,
  laborTotal,
  grandTotal,
  currency,
  conceptNumber,
  createdAt,
  status,
  className,
}: EstimateDocumentViewProps) {
  const t = useTranslations('common.estimate');
  const tConcept = useTranslations('concept');
  const locale = useLocale();

  const dateText = useMemo(() => {
    if (!createdAt) return '';
    try {
      return new Date(createdAt).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }, [createdAt, locale]);

  const money = (amount: number) =>
    formatEstimateMoney(amount, currency ?? 'USD');

  return (
    <div
      className={cn(
        // Same restrained two-layer drop shadow as AlbumCoverView; corner
        // radius is fixed at `rounded-lg` to match render cards, which the
        // estimate cards sit next to.
        '@container/cover relative h-full w-full rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.18),0_8px_16px_rgba(0,0,0,0.08)]',
        status === 'generating' && 'ring-2 ring-primary/60 ring-offset-0',
        className
      )}
    >
      <div className="absolute inset-0 rounded-lg bg-estimate-paper">
        <div className="flex h-full flex-col px-[8%]">
          <div className="h-[5%] shrink-0" />

          {/* Header — the localized word for "cost estimate". */}
          <p className="text-center text-[4.5cqi] font-medium uppercase leading-tight tracking-[0.12em] text-gray-500">
            {t('card.untitled')}
          </p>

          <div className="h-[4%] shrink-0" />

          {/* Decorative line-item stripes — a table-like sketch standing in
              for the real line items (label stripe + amount stripe). */}
          <div className="flex shrink-0 flex-col gap-[1.9cqi]">
            {[55, 68, 62, 48, 56].map((width, i) => (
              <div key={i} className="flex items-center justify-between">
                <div
                  className="h-[1.5cqi] rounded-full bg-gray-200"
                  style={{ width: `${width}%` }}
                />
                <div className="h-[1.5cqi] w-[14%] rounded-full bg-gray-300" />
              </div>
            ))}
          </div>

          <div className="h-[3.5%] shrink-0" />

          {/* Real figures: materials + labor subtotals. */}
          <div className="flex shrink-0 flex-col gap-[1.5cqi]">
            <div className="flex items-baseline justify-between gap-[2cqi] text-[4.5cqi] leading-tight">
              <span className="truncate text-gray-500">
                {t('editor.sections.materials')}
              </span>
              <span className="whitespace-nowrap font-mono font-medium text-gray-700 tabular-nums">
                {money(materialsTotal)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-[2cqi] text-[4.5cqi] leading-tight">
              <span className="truncate text-gray-500">
                {t('editor.sections.labor')}
              </span>
              <span className="whitespace-nowrap font-mono font-medium text-gray-700 tabular-nums">
                {money(laborTotal)}
              </span>
            </div>
          </div>

          <div className="h-[2.5%] shrink-0" />
          <div className="h-px shrink-0 bg-gray-300" />
          <div className="h-[2.5%] shrink-0" />

          {/* Grand total — the headline figure of the document. */}
          <div className="flex shrink-0 items-baseline justify-between gap-[2cqi] leading-tight">
            <span className="truncate text-[4.5cqi] font-medium text-gray-900">
              {t('editor.grandTotal')}
            </span>
            <span className="whitespace-nowrap font-mono text-[6.6cqi] font-bold text-gray-900 tabular-nums">
              {money(grandTotal)}
            </span>
          </div>

          <div className="flex-1" />

          {/* Footer — small "Design #N • date" so the card identifies its
              source design at a glance. */}
          <div className="flex items-center justify-center gap-[2cqi] text-[3.7cqi] leading-tight text-gray-500">
            {conceptNumber != null && conceptNumber > 0 && (
              <span>
                {tConcept('conceptNumber', { number: conceptNumber })}
              </span>
            )}
            {conceptNumber != null && conceptNumber > 0 && dateText && (
              <span>&bull;</span>
            )}
            {dateText && <span>{dateText}</span>}
          </div>

          <div className="h-[5%] shrink-0" />
        </div>
      </div>

      {/* Status overlay for non-ready estimates — same dark veil as the album
          cover so in-progress documents read identically across card types. */}
      {status !== 'ready' && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50">
          <div className="flex flex-col items-center gap-2">
            {status === 'failed' ? (
              <>
                <AlertCircle className="h-8 w-8 text-white" />
                <span className="text-list-item-value text-white">
                  {t('status.failed')}
                </span>
              </>
            ) : (
              <>
                <PulsingDots size="lg" className="text-white" />
                <span className="text-list-item-value text-white">
                  {t(`status.${status}`)}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
